import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, translations } from './translations'

const I18nContext = createContext({
  locale: DEFAULT_LOCALE,
  t: (key) => key,
  setLocale: () => {},
})

const getBrowserLocale = () => {
  if (typeof navigator === 'undefined') {
    return DEFAULT_LOCALE
  }

  const navigatorLanguages = navigator.languages || [navigator.language]
  const normalized = navigatorLanguages
    .map((lang) => lang && lang.split('-')[0].toLowerCase())
    .filter(Boolean)

  for (const lang of normalized) {
    if (SUPPORTED_LOCALES.includes(lang)) {
      return lang
    }
  }

  return DEFAULT_LOCALE
}

export function I18nProvider({ children }) {
  const [locale, setLocale] = useState(() => getBrowserLocale())

  useEffect(() => {
    if (!SUPPORTED_LOCALES.includes(locale)) {
      setLocale(DEFAULT_LOCALE)
    }
  }, [locale])

  const value = useMemo(() => {
    const messages = translations[locale] || translations[DEFAULT_LOCALE]

    const t = (key) => {
      const segments = key.split('.')
      let current = messages

      for (const segment of segments) {
        if (current && typeof current === 'object' && segment in current) {
          current = current[segment]
        } else {
          return key
        }
      }

      return typeof current === 'string' ? current : key
    }

    return {
      locale,
      setLocale,
      t,
      messages,
    }
  }, [locale])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export const useI18n = () => useContext(I18nContext)
